/**
 * Data mappers for converting GraphQL Support Ticket types to legacy interfaces
 *
 * These mappers ensure compatibility between the new GraphQL data structures
 * and existing component interfaces, allowing gradual migration.
 */

import type {
  IProblem,
  IProblemDetails,
  ITag,
  ITicket,
} from '../../../../ts/interface/problem.interface';
import type { SupportTicketGraphQL } from './useSupportTicketsRelay';
import type {
  SupportTicketDetailGraphQL,
  SupportTicketMaintenanceTicketGraphQL,
} from './useSupportTicketDetailRelay';
import type { TagGraphQL } from './useTagsRelay';

// ============================================================================
// Support Ticket List Mappers
// ============================================================================

/**
 * Map a GraphQL SupportTicket (from list) to the legacy IProblem interface
 *
 * @param ticket - The GraphQL support ticket data from list query
 * @returns IProblem compatible object
 */
export function mapRelaySupportTicketToIProblem(ticket: SupportTicketGraphQL): IProblem {
  return {
    id: ticket.id, // Use GraphQL UUID for routing
    state: ticket.state ?? 0,
    content: ticket.content || '',
    created: ticket.created || '',
    userName: ticket.user
      ? ticket.user.fullName || `${ticket.user.firstName} ${ticket.user.lastName}`
      : '',
    // userId holds the user's phone digits (User has no oldId); see detail mapper
    userId: ticket.user?.phone ? parseInt(ticket.user.phone.replace(/\D/g, ''), 10) : 0,
    tags: (ticket.tags as string[]) || [],
  };
}

/**
 * Map an array of GraphQL support tickets to IProblem array
 */
export function mapRelaySupportTicketsToIProblems(
  tickets: SupportTicketGraphQL[]
): IProblem[] {
  return tickets.map(mapRelaySupportTicketToIProblem);
}

// ============================================================================
// Support Ticket Detail Mappers
// ============================================================================

/**
 * Map a full GraphQL SupportTicket detail to the legacy IProblemDetails interface
 *
 * @param ticket - The GraphQL support ticket detail data
 * @returns IProblemDetails compatible object
 */
export function mapRelaySupportTicketToIProblemDetails(
  ticket: SupportTicketDetailGraphQL
): IProblemDetails {
  // Map tags from string[] to ITag[] format
  const mappedTags: ITag[] = (ticket.tags || []).map((tagName) => ({
    id: tagName, // Use tag name as ID since we only have the name string
    name: tagName,
    created_on: '',
    archived_on: '',
    system_info: {
      id: ticket.system?.id || '',
      name: ticket.system?.name || '',
      old_id: parseInt(ticket.system?.oldId || '0', 10),
    },
  }));

  return {
    id: ticket.oldId || ticket.id, // Use oldId for REST API compatibility
    gid: ticket.id,
    state: ticket.state ?? 0,
    content: ticket.content || '',
    created: ticket.created || '',
    closedDate: ticket.closedDate || null,
    owner: ticket.owner || null,
    type: ticket.type ?? 0,
    deviceId: ticket.deviceId || '',
    tags: mappedTags,

    // User info
    userName: ticket.user
      ? ticket.user.fullName || `${ticket.user.firstName} ${ticket.user.lastName}`
      : '',
    email: ticket.user?.email || '',
    // userId is used for routing to user page and for formatPhoneNumber
    // Since User doesn't have oldId, we use phone (which is the user identifier)
    userId: ticket.user?.phone ? parseInt(ticket.user.phone.replace(/\D/g, ''), 10) : 0,
    // Add user_gid for UUID-based routing
    user_gid: ticket.user?.id || null,

    // Hardware info
    hardware_id: ticket.hardware?.number?.toString() || null,
    hardware_type: ticket.hardware?.hardwareType?.name || null,
    // Add hardware_gid for organization navigation
    hardware_gid: ticket.hardware?.id || null,

    // Rental info
    rentalId: ticket.rental?.oldId || null,
    rental_gid: ticket.rental?.id || null,

    // System info
    system: ticket.system?.oldId || '',
    system_name: ticket.system?.name,
    // Add system_info for organization support
    system_info: ticket.system
      ? {
          id: ticket.system.id,
          old_id: ticket.system.oldId || '',
          name: ticket.system.name,
        }
      : undefined,

    // Communication log - parse JSON strings
    communication_log: parseCommunicationLog(ticket.communicationLog),

    // Maintenance tickets from the relationship
    maintenanceTickets: (ticket.maintenanceTickets || []).map(
      mapRelayMaintenanceTicketToITicket
    ),
  };
}

// ============================================================================
// Tag Mappers
// ============================================================================

/**
 * Map a GraphQL MaintenanceTicketTag to the legacy ITag interface
 *
 * @param tag - The GraphQL tag data
 * @returns ITag compatible object
 */
export function mapRelayTagToITag(tag: TagGraphQL): ITag {
  return {
    id: tag.id, // Use GraphQL UUID
    name: tag.name,
    created_on: tag.createdOn,
    archived_on: tag.archivedOn || '',
    system_info: {
      id: tag.system?.id || '',
      name: tag.system?.name || '',
      old_id: parseInt(tag.system?.oldId || '0', 10),
    },
  };
}

/**
 * Map an array of GraphQL tags to ITag array
 */
export function mapRelayTagsToITags(tags: TagGraphQL[]): ITag[] {
  return tags.map(mapRelayTagToITag);
}

// ============================================================================
// Maintenance Ticket Mappers
// ============================================================================

/**
 * Map a GraphQL MaintenanceTicket (from support ticket relationship) to ITicket
 */
function mapRelayMaintenanceTicketToITicket(
  ticket: SupportTicketMaintenanceTicketGraphQL
): ITicket {
  return {
    id: ticket.oldId ?? ticket.id,
    gid: ticket.id,
    due_on: ticket.dueOn,
    completed_on: ticket.completedOn ?? '',
    canceled_on: ticket.canceledOn ?? '',
    status: ticket.status ?? '',
    description: ticket.description,
  } as ITicket;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse communication log from JSON strings array
 *
 * The communication log is stored as an array of JSON strings that need
 * to be parsed to extract the actual log entries.
 */
function parseCommunicationLog(
  log: readonly (string | null)[] | null | undefined
): string | null {
  if (!log || log.length === 0) return null;

  // Filter out null entries and parse each JSON string
  const parsedEntries = log
    .filter((entry): entry is string => entry !== null)
    .map((entry) => {
      try {
        return JSON.parse(entry);
      } catch {
        return entry; // Return as-is if not valid JSON
      }
    });

  // Return as JSON string for backwards compatibility with existing component
  return JSON.stringify(parsedEntries);
}

// ============================================================================
// Extended IProblemDetails interface to include system_info
// ============================================================================

// Extend the IProblemDetails interface to include additional fields
declare module '../../../../ts/interface/problem.interface' {
  interface IProblemDetails {
    system_info?: {
      id: string;
      old_id: string;
      name: string;
    };
    hardware_gid?: string | null;
    user_gid?: string | null;
    maintenanceTickets?: ITicket[];
  }
}
